import { Stack, Typography } from '@mui/material';
import { some } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { Callout } from '@/components/notifications/Callout/Callout';
import { Loader } from '@/components/progress/Loader/Loader';
import { useFormContext } from '@/components/react-hook-form';
import { parseIntegerFormValue } from '@/components/utils/inputUtils';
import { diagnostics } from '@/utils/diagnostics';

import {
  HypotheticalSaleLoanFormKind,
  HypotheticalSaleLoanFormShape,
} from '../EstateWaterfallHypotheticalSaleLoanModal.types';
import { mapFormDataToCreateInput } from '../EstateWaterfallHypotheticalSaleLoanModal.utils';
import { useSaleLoanFormContext } from '../Form/context/SaleLoanForm.context';
import { useSaleLoanEstateWaterfallQuery } from './graphql/SaleLoanIllustration.generated';
import { useGrowthProfileOptions } from './hooks/useGrowthProfileOptions';
import { SaleLoanIllustrationAssumptionsButton } from './SaleLoanIllustrationAssumptionsButton';
import { SaleLoanIllustrationAssumptionsCard } from './SaleLoanIllustrationAssumptionsCard';
import { SaleLoanIllustrationBarChart } from './SaleLoanIllustrationBarChart';
import { PROJECTION_PSEUDO_ID } from './SaleLoanIllustrationContent.utils';
import { SaleLoanIllustrationDetails } from './SaleLoanIllustrationDetails/SaleLoanIllustrationDetails';

interface SaleLoanIllustrationContentProps {
  waterfallId: string;
  householdId: string;
  saleLoanId: string | null; // saleLoanId is null if we're creating a new loan and it's not yet persisted
  modalKind: HypotheticalSaleLoanFormKind;
}

function hasAllRequiredFundingData(formData: HypotheticalSaleLoanFormShape) {
  switch (formData.fundingKind) {
    case 'proRata':
      return formData.proRataAmount || formData.proRataPercent;
    case 'assets': {
      const hasSelectedItems =
        formData.selectedAssetClassesAndBusinessIds.length > 0;
      const hasDefinedAmount = some(
        formData.selectedAssetClassAndBusinessValues,
        (value) => Boolean(value?.amount)
      );
      const hasDefinedPercent = some(
        formData.selectedAssetClassAndBusinessValues,
        (value) => Boolean(value?.percent)
      );
      return hasSelectedItems && (hasDefinedAmount || hasDefinedPercent);
    }
    default:
      return false;
  }
}

export function SaleLoanIllustrationContent({
  waterfallId,
  householdId,
  saleLoanId,
  modalKind,
}: SaleLoanIllustrationContentProps) {
  const { setSimulatedWaterfall } = useSaleLoanFormContext();
  const [areAssumptionsOpen, setAreAssumptionsOpen] = useState(false);
  const { watch } = useFormContext<HypotheticalSaleLoanFormShape>();
  const formData = watch();
  // Both projections have to run against identical death-year assumptions, otherwise the
  // with-sale/loan and without-sale/loan comparison isn't apples to apples. We deliberately leave a
  // missing year as null instead of substituting one: the server clamps death years to at least the
  // current year and falls back to the waterfall's own years, which beats anything we'd invent.
  const deathYears = {
    firstGrantorDeathYear: parseIntegerFormValue(
      formData.firstGrantorDeathYear
    ),
    secondGrantorDeathYear: parseIntegerFormValue(
      formData.secondGrantorDeathYear
    ),
  };
  const { firstGrantorDeathYear, secondGrantorDeathYear } = deathYears;
  const projectionInput = useMemo(() => {
    // we don't throw inside mapFormDataToCreateInput for these, so we check them here to
    // make sure we don't start showing projection data too soon
    if (!hasAllRequiredFundingData(formData)) {
      return null;
    }

    if (!formData.paymentModel) {
      return null;
    }

    if (!firstGrantorDeathYear || firstGrantorDeathYear < 1900) {
      return null;
    }

    if (secondGrantorDeathYear && secondGrantorDeathYear < 1900) {
      return null;
    }

    if (!formData.growthProfileOverrideId) {
      return null;
    }

    try {
      return {
        firstGrantorDeathYear,
        secondGrantorDeathYear,
        growthProfileID: formData.growthProfileOverrideId,
        simulationInputs: {
          hypotheticalSaleLoans: [
            {
              // we skip the projection if we don't have a valid form data
              create: mapFormDataToCreateInput(
                formData,
                modalKind,
                waterfallId,
                householdId
              ),
              pseudoID: PROJECTION_PSEUDO_ID,
              replaces: saleLoanId ?? null,
            },
          ],
        },
      };
    } catch (error) {
      // we don't have a valid form data, so we can't create a projection. this is expected when the user
      // is still filling out the form and we have incomplete data, so we don't want to log or show an error
      diagnostics.debug(
        '[SALE/LOAN] Invalid or insufficient form data; not creating projection',
        { error }
      );
      return null;
    }
  }, [
    formData,
    firstGrantorDeathYear,
    secondGrantorDeathYear,
    modalKind,
    waterfallId,
    householdId,
    saleLoanId,
  ]);

  const { error, loading, refetch } = useSaleLoanEstateWaterfallQuery({
    fetchPolicy: 'no-cache',
    variables: {
      waterfallId,
      projectionInput: projectionInput!,
      noSaleLoanProjectionInput: {
        ...deathYears,
        growthProfileID: formData.growthProfileOverrideId,
        simulationInputs: {
          hypotheticalSaleLoans: [
            {
              create: null,
              pseudoID: PROJECTION_PSEUDO_ID,
              replaces: saleLoanId ?? null,
            },
          ],
        },
      },
    },
    onError: (error) => {
      diagnostics.error(
        'Error fetching projected waterfall for sale/loan designer',
        error
      );
    },
    // retrigger oncompleted when the we refetch the projections
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const waterfall = data?.estateWaterfalls.edges?.[0]?.node;
      if (waterfall) {
        setSimulatedWaterfall(waterfall);
      }
    },
    skip: !projectionInput, // Only skip if we don't have input data, not based on equality check
  });

  const {
    options: growthProfileOptions,
    loading: growthProfileOptionsLoading,
  } = useGrowthProfileOptions(householdId);

  // Guard refetch to only trigger when we have a valid projectionInput
  const safeRefetchProjections = useCallback(() => {
    if (!projectionInput) return; // nothing to refetch yet
    void refetch(); // same signature, just guarded
  }, [projectionInput, refetch]);

  const handleToggleAssumptions = () => {
    setAreAssumptionsOpen((areOpen) => !areOpen);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="title-xl">Illustration</Typography>
          {loading && <Loader circularProgressProps={{ size: 20 }} />}
        </Stack>
        <SaleLoanIllustrationAssumptionsButton
          onClick={handleToggleAssumptions}
          loading={growthProfileOptionsLoading}
          growthProfileOptions={growthProfileOptions}
        />
      </Stack>

      {areAssumptionsOpen && (
        <SaleLoanIllustrationAssumptionsCard
          refetchProjections={safeRefetchProjections}
          growthProfileOptions={growthProfileOptions}
        />
      )}

      <SaleLoanIllustrationBarChart />

      {error && (
        <Callout severity="error">
          There was an error fetching the projected details. Please refresh the
          page and try again.
        </Callout>
      )}

      <SaleLoanIllustrationDetails />
    </Stack>
  );
}
